import {useSidebar} from '@/contexts/SidebarContext/SidebarContext';
import {
  SIDEBAR_CATEGORY_LABELS,
  SidebarCategoryItemProps,
} from '@/contexts/SidebarContext/types';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {useLocation, useNavigate} from 'react-router-dom';

type ExtendedSidebarCategoryItemProps = SidebarCategoryItemProps & {
  isPinned: boolean;
};

export const SidebarCategoryItem = (
  props: ExtendedSidebarCategoryItemProps,
) => {
  const theme = useTheme();
  const {
    selectedCategory,
    setSelectedCategory,
    isSubSidebarCollapsed,
    setSubSidebarCollapsed,
  } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setSelectedCategory(props.label);
    if (isSubSidebarCollapsed) {
      setSubSidebarCollapsed(false);
    }

    if (props.url) {
      navigate(props.url);
    }
  };

  const isSelected = props.url
    ? location.pathname === props.url
    : selectedCategory === props.label;

  const label = SIDEBAR_CATEGORY_LABELS[props.label];

  if (props.isPinned) {
    return (
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: '6px',
          cursor: 'pointer',
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : 'transparent',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: isSelected
              ? theme.palette.action.selected
              : theme.palette.action.hover,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            '& svg, & .material-symbols-outlined': {
              fontSize: 18,
              color: isSelected
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            },
          }}
        >
          {props.BadgeComponent ? (
            <props.BadgeComponent>{props.icon}</props.BadgeComponent>
          ) : (
            props.icon
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            fontWeight: isSelected ? 500 : 400,
            lineHeight: 1.2,
            letterSpacing: '0.1px',
            color: isSelected
              ? theme.palette.text.primary
              : theme.palette.text.secondary,
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  }

  const iconContent = props.BadgeComponent ? (
    <props.BadgeComponent>{props.icon}</props.BadgeComponent>
  ) : (
    props.icon
  );

  return (
    <Tooltip title={label} placement="right">
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          width: 36,
          height: 36,
          p: 0.5,
          borderRadius: '6px',
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : 'transparent',
          '&:hover': {
            backgroundColor: isSelected
              ? theme.palette.action.selected
              : theme.palette.action.hover,
          },
          '& svg, & .material-symbols-outlined': {
            fontSize: 18,
            color: isSelected
              ? theme.palette.text.primary
              : theme.palette.text.secondary,
          },
        }}
      >
        {iconContent}
      </IconButton>
    </Tooltip>
  );
};
